import { forwardRef, Ref, useEffect, useRef, useState } from 'react';
import {
	Box,
	ButtonBase,
	CircularProgress,
	Grid2,
	IconButton,
	Menu,
	MenuItem,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import _, { throttle } from 'lodash';
import { useMainCustomStyle } from '../../assets/theme/Theme';
import {
	ArrowDownIcon,
	BackArrowIcon,
	CheckIcon,
	ChevronDownIcon,
	CloseIcon,
	LanguageIcon,
	PDFIcon,
	SendChatIcon,
	SendLinkIcon,
	SuggestReplyIcon,
} from '../../assets/icons/Icons';
import SideNavigationBar from '../../components/SideNavigationBar';
import Avatar from '../../assets/images/Avatar.png';
import {
	downloadMediaFile,
	fetchChatNumbers,
	fetchChats,
	fetchConsultantSuggestedResponse,
	markMessageAsUnread,
	onEnableAgentMode,
	onFetchUploadInfo,
	onResetChatMsgCount,
	sendMessage,
	sendUploadIdAndMessage,
	updateChatLanguage,
	uploadPdf,
} from '../../services/ApiService';
import {
	ChatData,
	ChatList,
	Chats,
	ChatsResponse,
	ConsultantDraftsResponse,
	ErrorStatus,
	Tag,
	TaggedMessage,
} from '../../types';
import {
	convertTo12HourTime,
	dateFormatter,
	formatLlmCost,
	truncateForPreview,
} from '../../Utils/Helpers';
import ChatRow from '../../components/ChatRow';
import CustomSwitch from '../../components/CustomSwitch';
import { GREY, MAIN_THEME_COLOR, WHITE } from '../../assets/theme/Colors';
import {
	CHAT_LANGUAGE_FILTERS,
	CHAT_LANGUAGE_OPTIONS,
	CHAT_SEARCH_TAGS,
} from '../../Utils/Enums';
import ChatNumbersList from './ChatNumbersList';
import { getAgencyId, getUserEmail, getUserId } from '../../Utils/AuthUtil';
import { getMsgReadStatus } from '../../context/MainProvider';
import { onAddClickStat } from '../../services/UserService';

interface ChatWindowProps {
	isChatView: boolean;
	chatData: { [index: string]: Array<ChatData> };
	loading: boolean;
	message: string;
	setMessage: (value: string) => void;
	isConsultantModeOn: boolean;
	onChangeConsultantMode: (
		event: React.ChangeEvent<HTMLInputElement>,
	) => void;
	onSendChat: () => void;
	messageError: ErrorStatus;
	isFileSelected: boolean;
	selectedFile: File | null;
	onCancelSelectedFile: () => void;
	onSelectAttachments: (event: React.ChangeEvent<HTMLInputElement>) => void;
	previewUrl: string;
	onDownloadFile: (url: string) => void;
	newMessageCount: number;
	onScrollDown: () => void;
	isInitialFetchDone: boolean;
	handleScroll: () => void;
	isSending: boolean;
	isDownloading: boolean;
	downloadingFile: string;
	isFetchingSuggestion: boolean;
	onFetchConsultantSuggestion: () => void;
	taggedMessage: TaggedMessage | null;
	onTagMessage: (message: TaggedMessage) => void;
	onCancelTaggedMessage: () => void;
	onMarkMessageUnread: (wamid: string) => void;
	messageInputRef: Ref<HTMLTextAreaElement>;
	unreadFromWamid: string;
	unreadCount: number;
}

interface ChatWindowMainProps {
	isChatWindow: boolean;
	selectedChatData: { [index: string]: Array<ChatData> };
	isChatLoading: boolean;
	isMobile: boolean;
	onclickGoBack: () => void;
	message: string;
	setMessage: (value: string) => void;
	isConsultantModeOn: boolean;
	onChangeConsultantMode: (
		event: React.ChangeEvent<HTMLInputElement>,
	) => void;
	selectedChat: SelectedChat;
	onSendChat: () => void;
	messageError: ErrorStatus;
	isFileSelected: boolean;
	selectedFile: File | null;
	onCancelSelectedFile: () => void;
	onSelectAttachments: (event: React.ChangeEvent<HTMLInputElement>) => void;
	previewUrl: string;
	onDownloadFile: (url: string) => void;
	newMessageCount: number;
	onScrollDown: () => void;
	isInitialFetchDone: boolean;
	handleScroll: () => void;
	isSending: boolean;
	isDownloading: boolean;
	downloadingFile: string;
	isFetchingSuggestion: boolean;
	onFetchConsultantSuggestion: () => void;
	taggedMessage: TaggedMessage | null;
	onTagMessage: (message: TaggedMessage) => void;
	onCancelTaggedMessage: () => void;
	onMarkMessageUnread: (wamid: string) => void;
	currentChatLanguage: string;
	onSelectChatLanguage: (language: string) => void;
	currentChatCost?: number | string;
	messageInputRef: Ref<HTMLTextAreaElement>;
	unreadFromWamid: string;
	unreadCount: number;
}

interface SelectedChat {
	phoneNumber: string;
	name: string;
	isConsultantNeeded: boolean;
	phoneNumberExtra: string;
}

const ERROR_INITIAL_STATES: ErrorStatus = {
	status: false,
	msg: '',
};

function getFilteredChatList(
	allData: Array<ChatList>,
	tag: string,
	language: string,
	search: string,
): Array<ChatList> {
	let filtered = allData;

	if (tag === 'Consultant Mode') {
		filtered = filtered.filter(item => item.isConsultantNeeded);
	}

	if (language !== 'All') {
		filtered = filtered.filter(item => item.language === language);
	}

	if (search !== '') {
		const numberRegex = /\d/;
		const searchField = numberRegex.test(search) ? 'phoneNumber' : 'name';
		filtered = filtered.filter(item =>
			item[searchField].toLowerCase().includes(search.toLowerCase()),
		);
	}

	return filtered;
}

// Find where the unread divider goes, falls back to the badge count if the server wamid is missing
function getUnreadDivider(
	messages: Array<ChatData>,
	unreadFromWamid: string | undefined,
	badgeUnreadCount: number,
): { wamid: string; count: number } | null {
	const serverIndex = unreadFromWamid
		? messages.findIndex(item => item.wamid === unreadFromWamid)
		: -1;

	if (serverIndex !== -1 && unreadFromWamid) {
		const incomingCount = messages
			.slice(serverIndex)
			.filter(item => item.type === 'human').length;
		return {
			wamid: unreadFromWamid,
			count: badgeUnreadCount || incomingCount || 1,
		};
	}

	if (badgeUnreadCount <= 0) {
		return null;
	}

	const incoming = messages.filter(item => item.type === 'human');
	const firstUnread =
		incoming[Math.max(incoming.length - badgeUnreadCount, 0)];

	if (!firstUnread?.wamid) {
		return null;
	}

	return { wamid: firstUnread.wamid, count: badgeUnreadCount };
}

const ChatBodyWindow = forwardRef<HTMLDivElement, ChatWindowProps>(
	function ChatBodyWindow(
		{
			isChatView,
			chatData,
			loading,
			message,
			setMessage,
			isConsultantModeOn,
			onChangeConsultantMode,
			onSendChat,
			messageError,
			isFileSelected,
			selectedFile,
			onCancelSelectedFile,
			onSelectAttachments,
			previewUrl,
			onDownloadFile,
			newMessageCount,
			onScrollDown,
			isInitialFetchDone,
			handleScroll,
			isSending,
			isDownloading,
			downloadingFile,
			isFetchingSuggestion,
			onFetchConsultantSuggestion,
			taggedMessage,
			onTagMessage,
			onCancelTaggedMessage,
			onMarkMessageUnread,
			messageInputRef,
			unreadFromWamid,
			unreadCount,
		}: ChatWindowProps,
		ref,
	) {
		if (isChatView && !loading) {
			return (
				<Grid2 width={'100%'} paddingX={'5px'}>
					<Box
						ref={ref}
						onScroll={handleScroll}
						sx={{
							overflowY: 'auto',
							height: isFileSelected
								? '47vh'
								: taggedMessage
									? '64vh'
									: '70vh',
							width: '100%',
							'&::-webkit-scrollbar': {
								display: 'none', // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
							},
							'&-ms-overflow-style:': {
								display: 'none', // Hide the scrollbar for IE
							},
						}}>
						<Grid2 width={'100%'} padding={'10px'}>
							{Object.keys(chatData).map((item, index) => {
								return (
									<Grid2 key={index}>
										<Typography
											variant="body2"
											fontWeight={'500'}
											textAlign={'center'}>
											{dateFormatter(item)}
										</Typography>
										{chatData[item].map((chat, value) => {
											return (
												<Grid2
													key={value}
													width={'100%'}
													data-wamid={chat.wamid}>
													{unreadFromWamid !== '' &&
														chat.wamid ===
															unreadFromWamid && (
															<Grid2
																container
																width={'100%'}
																justifyContent={
																	'center'
																}
																alignItems={
																	'center'
																}
																sx={{
																	backgroundColor:
																		'rgba(33, 193, 122, 0.08)',
																	paddingY:
																		'6px',
																	marginY:
																		'10px',
																}}>
																<Typography
																	fontSize={
																		'11px'
																	}
																	fontWeight={
																		600
																	}
																	color={
																		MAIN_THEME_COLOR
																	}
																	sx={{
																		textTransform:
																			'uppercase',
																		letterSpacing:
																			'0.5px',
																	}}>
																	{`${unreadCount} unread ${unreadCount === 1 ? 'message' : 'messages'}`}
																</Typography>
															</Grid2>
														)}
													<ChatRow
														position={
															chat.type ===
															'human'
																? 'left'
																: 'right'
														}
														text={chat.text}
														chatTime={chat.time}
														fileName={chat.fileName}
														fileUrl={chat.fileUrl}
														onDownload={
															onDownloadFile
														}
														caption={chat.caption}
														chatOwner={
															chat.sender ||
															chat.type
														}
														isDownloading={
															isDownloading
														}
														downloadingFile={
															downloadingFile
														}
														wamid={chat.wamid}
														llmCost={chat.llmCost}
														isThisReplyMsg={
															chat.isThisReplyMsg
														}
														repliedMsg={
															chat.repliedMsg
														}
														repliedMsgOwner={
															chat.repliedMsgOwner
														}
														onReply={onTagMessage}
														isReplyDisabled={
															!isConsultantModeOn
														}
														onMarkUnread={
															onMarkMessageUnread
														}
													/>
												</Grid2>
											);
										})}
									</Grid2>
								);
							})}
						</Grid2>
					</Box>
					{messageError.status && (
						<Grid2 container>
							<Typography
								fontSize={'10px'}
								color={'red'}
								marginLeft={'20px'}>
								{messageError.msg}
							</Typography>
						</Grid2>
					)}
					{isInitialFetchDone && newMessageCount > 0 && (
						<Grid2 container justifyContent={'center'}>
							<IconButton
								disableRipple
								disableFocusRipple
								sx={{
									padding: '5px',
									flexDirection: 'row',
									borderRadius: '5px',
									backgroundColor: MAIN_THEME_COLOR,
								}}
								onClick={onScrollDown}>
								<ArrowDownIcon />
								<Typography variant="caption" color={'#FFFFFF'}>
									{`${newMessageCount} new ${newMessageCount === 1 ? 'message' : 'messages'}`}
								</Typography>
							</IconButton>
						</Grid2>
					)}
					{isFileSelected && previewUrl && (
						<Box
							sx={{
								position: 'relative',
								backgroundColor: '#F2F4F5',
								flexDirection: 'row',
								justifyContent: 'space-between',
								width: { md: '74.5%', xs: '100%' },
								marginRight: '20px',
								borderTopLeftRadius: '20px',
								borderTopRightRadius: '20px',
								borderBottomLeftRadius: '0px',
								borderBottomRightRadius: '0px',
							}}
							padding={'10px'}>
							<Grid2
								component={'div'}
								sx={{
									width: '90%',
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									flexDirection: 'column',
								}}>
								<img
									src={previewUrl}
									alt="Preview"
									style={{
										width: '95%',
										maxHeight: '120px',
										objectFit: 'contain',
									}}
								/>
								<Typography
									style={{
										wordWrap: 'break-word',
									}}
									color={'#292D32'}
									fontSize={'12px'}>
									{selectedFile?.name}
								</Typography>
								<Typography
									color={'#A9ACB4'}
									marginRight={'5px'}
									fontSize={'10px'}>
									{(
										(selectedFile?.size
											? selectedFile.size
											: 0) / 1024
									).toFixed(2)}{' '}
									KB
								</Typography>
							</Grid2>
							<IconButton
								sx={{
									position: 'absolute',
									top: '20px',
									right: '20px',
									padding: '0px',
								}}
								onClick={onCancelSelectedFile}>
								<CloseIcon />
							</IconButton>
						</Box>
					)}
					{isFileSelected && selectedFile && !previewUrl && (
						<Grid2
							container
							size={{ xs: 12, md: 9 }}
							sx={{
								backgroundColor: '#F2F4F5',
								justifyContent: 'space-between',
								width: { md: '74.5%', xs: '100%' },
								marginRight: '20px',
								borderTopLeftRadius: '20px',
								borderTopRightRadius: '20px',
								borderBottomLeftRadius: '0px',
								borderBottomRightRadius: '0px',
							}}
							padding={'20px'}>
							<Grid2
								component={'div'}
								sx={{
									width: '90%',
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									flexDirection: 'column',
								}}>
								<PDFIcon />
								<Typography
									style={{
										wordWrap: 'break-word',
									}}
									color={'#292D32'}
									fontSize={'12px'}>
									{selectedFile?.name}
								</Typography>
								<Typography
									color={'#A9ACB4'}
									marginRight={'5px'}
									fontSize={'10px'}>
									{(
										(selectedFile?.size
											? selectedFile.size
											: 0) / 1024
									).toFixed(2)}{' '}
									KB
								</Typography>
							</Grid2>
							<IconButton
								sx={{ padding: '0px', height: '10%' }}
								onClick={onCancelSelectedFile}>
								<CloseIcon />
							</IconButton>
						</Grid2>
					)}
					<Grid2
						container
						width={'100%'}
						spacing={2}
						alignItems={'flex-end'}>
						{isConsultantModeOn ? (
							<Grid2
								component={'div'}
								size={{ xs: 12, md: 9 }}
								sx={{
									display: 'flex',
									flexDirection: 'column',
									minWidth: 0,
									maxWidth: '100%',
									marginY: isFileSelected ? '0px' : '5px',
									bgcolor: '#F2F4F5',
									borderTopLeftRadius: isFileSelected
										? '0px'
										: '20px',
									borderTopRightRadius: isFileSelected
										? '0px'
										: '20px',
									borderBottomLeftRadius: '20px',
									borderBottomRightRadius: '20px',
									overflow: 'hidden',
								}}>
								{taggedMessage && (
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'row',
											justifyContent: 'space-between',
											alignItems: 'center',
											width: '100%',
											minWidth: 0,
											maxWidth: '100%',
											overflow: 'hidden',
											boxSizing: 'border-box',
											backgroundColor: '#E3E8EB',
											borderLeft: `4px solid ${MAIN_THEME_COLOR}`,
											paddingX: '10px',
											paddingY: '8px',
										}}>
										<Grid2
											component={'div'}
											sx={{
												flex: 1,
												minWidth: 0,
												overflow: 'hidden',
											}}>
											<Typography
												fontSize={'12px'}
												fontWeight={'600'}
												color={MAIN_THEME_COLOR}>
												{taggedMessage.chatOwner ===
												'human'
													? 'Customer'
													: 'You'}
											</Typography>
											<Typography
												fontSize={'12px'}
												color={'#4C4C4C'}
												sx={{
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}>
												{taggedMessage.isFile
													? taggedMessage.fileName ||
														'Attachment'
													: taggedMessage.text}
											</Typography>
										</Grid2>
										<IconButton
											sx={{
												padding: '0px',
												flexShrink: 0,
											}}
											onClick={onCancelTaggedMessage}>
											<CloseIcon />
										</IconButton>
									</Box>
								)}
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										padding: '5px',
									}}>
									<TextField
										type="text"
										variant="standard"
										multiline
										inputRef={messageInputRef}
										disabled={isFetchingSuggestion}
										placeholder={
											isFetchingSuggestion
												? 'Generating response...'
												: 'Type your message here...'
										}
										sx={{
											width: { md: '93%', xs: '82%' },
											'& fieldset': {
												border: 'none',
											},
										}}
										slotProps={{
											input: {
												style: {
													color: '#000000',
													paddingLeft: '5px',
													fontSize: '14px',
												},
												disableUnderline: true,
											},
										}}
										onKeyDown={event => {
											const isMobile =
												'ontouchstart' in window ||
												/Mobi|Android/i.test(
													navigator.userAgent,
												);
											if (event.key === 'Enter') {
												if (
													!event.shiftKey &&
													!isMobile
												) {
													event.preventDefault();
													onSendChat();
												}
											}
										}}
										value={message}
										onChange={e =>
											setMessage(e.target.value)
										}
									/>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'row',
											alignSelf: 'flex-end',
											alignItems: 'center',
											marginLeft: '8px',
										}}>
										{isFetchingSuggestion ? (
											<CircularProgress
												sx={{
													color: MAIN_THEME_COLOR,
													marginRight: '5px',
												}}
												size={20}
											/>
										) : (
											<IconButton
												title="Fetch suggested response"
												onClick={
													onFetchConsultantSuggestion
												}>
												<SuggestReplyIcon />
											</IconButton>
										)}
										<IconButton
											component="label"
											disabled={isFetchingSuggestion}>
											<SendLinkIcon />
											<input
												hidden
												type="file"
												disabled={isFetchingSuggestion}
												onChange={onSelectAttachments}
											/>
										</IconButton>
										{isSending ? (
											<CircularProgress
												sx={{
													color: '#21C17A',
													marginRight: '5px',
												}}
												size={20}
											/>
										) : (
											<IconButton
												disabled={
													isFetchingSuggestion ||
													(isFileSelected
														? false
														: message === ''
															? true
															: false)
												}
												onClick={onSendChat}>
												<SendChatIcon />
											</IconButton>
										)}
									</Box>
								</Box>
							</Grid2>
						) : (
							<Grid2
								component={'div'}
								size={{ xs: 12, md: 9 }}
								sx={{
									display: 'flex',
									alignItems: 'center',
									marginY: '5px',
									padding: '5px',
									bgcolor: '#FFFFFF',
								}}>
								<Grid2 height={'36px'} />
							</Grid2>
						)}
						<Grid2
							component={'div'}
							size={{ xs: 7, md: 3 }}
							sx={{
								border: `1px solid ${isConsultantModeOn ? MAIN_THEME_COLOR : GREY}`,
								borderRadius: '5px',
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								paddingLeft: '4px',
								height: 45,
								marginY: '5px',
								backgroundColor: isConsultantModeOn
									? '#DEECED'
									: WHITE,
								ml: 'auto',
							}}>
							<Typography
								fontSize={'14px'}
								color={
									isConsultantModeOn ? MAIN_THEME_COLOR : GREY
								}
								sx={{ marginRight: '5px' }}>
								Consultant Mode
							</Typography>
							<CustomSwitch
								isChecked={isConsultantModeOn}
								handleChange={onChangeConsultantMode}
							/>
						</Grid2>
					</Grid2>
				</Grid2>
			);
		} else if (!isChatView && loading) {
			return (
				<Grid2
					container
					width={'100%'}
					height={'70vh'}
					justifyContent={'center'}
					alignItems={'center'}>
					<CircularProgress sx={{ color: MAIN_THEME_COLOR }} />
				</Grid2>
			);
		} else {
			return (
				<Grid2
					container
					width={'100%'}
					height={'70vh'}
					justifyContent={'center'}
					alignItems={'center'}>
					<Typography fontSize={'16px'} fontWeight={'400'}>
						No chat data available
					</Typography>
				</Grid2>
			);
		}
	},
);

function ChatWindowMain(
	{
		isChatWindow,
		selectedChatData,
		isChatLoading,
		isMobile,
		onclickGoBack,
		message,
		setMessage,
		isConsultantModeOn,
		onChangeConsultantMode,
		selectedChat,
		onSendChat,
		messageError,
		isFileSelected,
		selectedFile,
		onCancelSelectedFile,
		onSelectAttachments,
		previewUrl,
		onDownloadFile,
		newMessageCount,
		onScrollDown,
		isInitialFetchDone,
		handleScroll,
		isSending,
		isDownloading,
		downloadingFile,
		isFetchingSuggestion,
		onFetchConsultantSuggestion,
		taggedMessage,
		onTagMessage,
		onCancelTaggedMessage,
		onMarkMessageUnread,
		currentChatLanguage,
		onSelectChatLanguage,
		currentChatCost,
		messageInputRef,
		unreadFromWamid,
		unreadCount,
	}: ChatWindowMainProps,
	messagesEndRef: Ref<HTMLDivElement>,
) {
	const [languageMenuAnchorEl, setLanguageMenuAnchorEl] =
		useState<HTMLElement | null>(null);

	function handleLanguageSelect(language: string) {
		setLanguageMenuAnchorEl(null);
		onSelectChatLanguage(language);
	}

	function isToPhoneNumberTooLong(number: string) {
		if (selectedChat.name != 'Unknown') {
			if (isMobile && number.length > 21) {
				return number.slice(0, 21) + '...';
			} else {
				return number;
			}
		}

		return '';
	}

	return (
		<Grid2 component="div" size={{ xs: 12, md: 8 }}>
			<Grid2
				bgcolor={'#FFFFFF'}
				borderRadius={'5px'}
				sx={{ height: '100%' }}>
				<Grid2
					container
					justifyContent={'space-between'}
					alignItems={'center'}
					padding={'10px'}>
					{selectedChat.phoneNumber !== '' && (
						<Grid2 container alignItems={'center'}>
							{isMobile && (
								<IconButton onClick={onclickGoBack}>
									<BackArrowIcon />
								</IconButton>
							)}
							<Box
								component={'img'}
								src={Avatar}
								width={40}
								height={40}
							/>
							<Grid2
								component={'div'}
								flexDirection={'row'}
								paddingLeft="15px">
								<Typography
									fontWeight={'500'}
									color="#000000"
									fontSize={'14px'}>
									{selectedChat.name != 'Unknown'
										? selectedChat.name
										: selectedChat.phoneNumber}
								</Typography>
								<Typography
									fontWeight={'500'}
									color="#7c7c7c"
									fontSize={'12px'}>
									{isToPhoneNumberTooLong(
										selectedChat.phoneNumberExtra,
									)}
								</Typography>
							</Grid2>
						</Grid2>
					)}
					{selectedChat.phoneNumber !== '' && (
						<Grid2
							container
							alignItems={'center'}
							sx={{ gap: '8px' }}>
							{formatLlmCost(currentChatCost) && (
								<Tooltip title="Total LLM cost for this conversation">
									<Typography
										fontSize={'12px'}
										fontWeight={600}
										color={MAIN_THEME_COLOR}
										sx={{
											backgroundColor:
												'rgba(33, 193, 122, 0.12)',
											borderRadius: '12px',
											paddingX: '10px',
											paddingY: '3px',
										}}>
										{formatLlmCost(currentChatCost)}
									</Typography>
								</Tooltip>
							)}
							<Tooltip title="Set chat language">
								<ButtonBase
									onClick={event =>
										setLanguageMenuAnchorEl(
											event.currentTarget,
										)
									}
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '4px',
										paddingX: '8px',
										paddingY: '4px',
										borderRadius: '999px',
										border: `1px solid ${
											currentChatLanguage
												? MAIN_THEME_COLOR
												: '#D9D9D9'
										}`,
										backgroundColor: currentChatLanguage
											? 'rgba(33, 193, 122, 0.08)'
											: 'transparent',
									}}>
									<LanguageIcon
										color={
											currentChatLanguage
												? MAIN_THEME_COLOR
												: '#8E8E8E'
										}
									/>
									<Typography
										fontSize={'12px'}
										fontWeight={600}
										color={
											currentChatLanguage
												? MAIN_THEME_COLOR
												: '#8E8E8E'
										}>
										{currentChatLanguage || 'Language'}
									</Typography>
									<ChevronDownIcon
										color={
											currentChatLanguage
												? MAIN_THEME_COLOR
												: '#8E8E8E'
										}
									/>
								</ButtonBase>
							</Tooltip>
							<Menu
								anchorEl={languageMenuAnchorEl}
								open={Boolean(languageMenuAnchorEl)}
								onClose={() => setLanguageMenuAnchorEl(null)}>
								{CHAT_LANGUAGE_OPTIONS.map(
									(language, index) => {
										const isSelected =
											currentChatLanguage === language;
										return (
											<MenuItem
												key={index}
												selected={isSelected}
												onClick={() =>
													handleLanguageSelect(
														language,
													)
												}
												sx={{
													fontSize: '13px',
													fontWeight: isSelected
														? 600
														: 400,
													color: isSelected
														? MAIN_THEME_COLOR
														: 'inherit',
													justifyContent:
														'space-between',
													gap: '16px',
												}}>
												{language}
												{isSelected && (
													<CheckIcon
														color={MAIN_THEME_COLOR}
													/>
												)}
											</MenuItem>
										);
									},
								)}
							</Menu>
						</Grid2>
					)}
				</Grid2>
				<Grid2 container>
					<ChatBodyWindow
						isChatView={isChatWindow}
						chatData={selectedChatData}
						ref={messagesEndRef}
						loading={isChatLoading}
						message={message}
						setMessage={setMessage}
						isConsultantModeOn={isConsultantModeOn}
						onChangeConsultantMode={onChangeConsultantMode}
						onSendChat={onSendChat}
						messageError={messageError}
						isFileSelected={isFileSelected}
						selectedFile={selectedFile}
						onCancelSelectedFile={onCancelSelectedFile}
						onSelectAttachments={onSelectAttachments}
						previewUrl={previewUrl}
						onDownloadFile={onDownloadFile}
						newMessageCount={newMessageCount}
						onScrollDown={onScrollDown}
						isInitialFetchDone={isInitialFetchDone}
						handleScroll={handleScroll}
						isSending={isSending}
						isDownloading={isDownloading}
						downloadingFile={downloadingFile}
						isFetchingSuggestion={isFetchingSuggestion}
						onFetchConsultantSuggestion={
							onFetchConsultantSuggestion
						}
						taggedMessage={taggedMessage}
						onTagMessage={onTagMessage}
						onCancelTaggedMessage={onCancelTaggedMessage}
						onMarkMessageUnread={onMarkMessageUnread}
						messageInputRef={messageInputRef}
						unreadFromWamid={unreadFromWamid}
						unreadCount={unreadCount}
					/>
				</Grid2>
			</Grid2>
		</Grid2>
	);
}

const ForwardedChatWindowMain = forwardRef(ChatWindowMain);

function Conversations() {
	const mainDivStyle = useMainCustomStyle();
	const theme = useTheme();
	const userId = getUserId();
	const orgId = getAgencyId();
	const { fetchMsgDetails } = getMsgReadStatus();
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const messageInputRef = useRef<HTMLTextAreaElement>(null);
	const [selectedChat, setSelectedChat] = useState<SelectedChat>({
		phoneNumber: '',
		name: '',
		isConsultantNeeded: false,
		phoneNumberExtra: '',
	});
	const [isChatWindow, setIsChatWindow] = useState(false);
	const [selectedChatData, setSelectedChatData] = useState<{
		[index: string]: Array<ChatData>;
	}>({});
	const [chatListData, setChatListData] = useState<Array<ChatList>>([]);
	const [chatListAllData, setChatListAllData] = useState<Array<ChatList>>([]);
	const [loading, setLoading] = useState(false);
	const [isChatLoading, setIsChatLoading] = useState(false);
	const [showNumberList, setShowNumberList] = useState(true);
	const [message, setMessage] = useState('');
	const [enableConsultantMode, setEnableConsultantMode] = useState(false);
	const [tagList, setTagList] = useState<Array<Tag>>([]);
	const [selectedTag, setSelectedTag] = useState('All');
	const [languageTagList, setLanguageTagList] = useState<Array<Tag>>([]);
	const [selectedLanguage, setSelectedLanguage] = useState('All');
	const [messageError, setMessageError] =
		useState<ErrorStatus>(ERROR_INITIAL_STATES);
	const [isSending, setIsSending] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [isLoadingChatFirstTime, setIsLoadingChatFirstTime] = useState(true);
	const [isFileSelected, setIsFileSelected] = useState(false);
	const [file, setFile] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState('');
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const previousChatDataRef = useRef<{ [index: string]: Array<ChatData> }>(
		{},
	);
	const [newMessageCount, setNewMessageCount] = useState(0);
	const enableAutoScrollRef = useRef<boolean>(false);
	const isInitialFetchDoneRef = useRef<boolean>(false);
	const isNewMessageSendRef = useRef<boolean>(false);
	const isCurrentChatNumberRef = useRef<string>('');
	// Open chat number, used to ignore responses from a previously opened chat
	const openChatNumberRef = useRef<string>('');
	const [isDownloading, setIsDownloading] = useState(false);
	const [downloadingFile, setDownloadingFile] = useState('');
	const [isFetchingSuggestion, setIsFetchingSuggestion] = useState(false);
	const [taggedMessage, setTaggedMessage] = useState<TaggedMessage | null>(
		null,
	);
	const [currentChatLanguage, setCurrentChatLanguage] = useState('');
	const [unreadFromWamid, setUnreadFromWamid] = useState('');
	const [unreadCount, setUnreadCount] = useState(0);
	const [currentChatCost, setCurrentChatCost] = useState<
		number | string | undefined
	>(undefined);
	const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
	const numberListTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);

	useEffect(() => {
		fetchChatNumbersList();
	}, []);

	useEffect(() => {
		const scrollToBottom = () => {
			if (messagesEndRef.current) {
				messagesEndRef.current.style.scrollBehavior = 'auto';
				messagesEndRef.current.scrollTop =
					messagesEndRef.current.scrollHeight;

				setTimeout(() => {
					if (messagesEndRef.current) {
						messagesEndRef.current.style.scrollBehavior = 'smooth';
					}
				}, 100);
			}
		};

		const scrollToUnreadBanner = () => {
			if (!messagesEndRef.current || !unreadFromWamid) {
				return false;
			}

			const target = messagesEndRef.current.querySelector(
				`[data-wamid="${unreadFromWamid}"]`,
			);

			if (!target) {
				return false;
			}

			messagesEndRef.current.style.scrollBehavior = 'auto';
			(target as HTMLElement).scrollIntoView({ block: 'start' });

			setTimeout(() => {
				if (messagesEndRef.current) {
					messagesEndRef.current.style.scrollBehavior = 'smooth';
				}
			}, 100);

			return true;
		};

		// Scroll to the unread divider on open, otherwise to the bottom
		if (isLoadingChatFirstTime) {
			if (!scrollToUnreadBanner()) {
				scrollToBottom();
			}
		} else if (enableAutoScrollRef.current) {
			scrollToBottom();
		}

		const handleResize = () => {
			setTimeout(scrollToBottom, 200); // delay for layout to settle in mobile
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [selectedChatData, enableAutoScrollRef.current, unreadFromWamid]);

	//TODO: This is a short term fix for real-time update on chat conversation
	//Remove this useEffect once the actual solution is implemented
	useEffect(() => {
		return () => {
			if (timeoutId.current) {
				clearTimeout(timeoutId.current);
			}

			if (numberListTimeoutId.current) {
				clearTimeout(numberListTimeoutId.current);
			}
		};
	}, []);

	useEffect(() => {
		const chat = chatListData.find(
			item => item.phoneNumber === selectedChat.phoneNumber,
		);

		setSelectedChat(prevState => ({
			...prevState,
			name: chat?.name ? chat?.name : prevState.name,
			isConsultantNeeded: chat?.isConsultantNeeded
				? chat?.isConsultantNeeded
				: false,
		}));

		setEnableConsultantMode(
			chat?.isConsultantNeeded ? chat?.isConsultantNeeded : false,
		);

		setCurrentChatCost(chat?.llmCost);
	}, [chatListData, selectedChatData]);

	useEffect(() => {
		if (searchTerm === '') {
			fetchChatNumbersList(false);
		}
	}, [searchTerm]);

	//TODO: This is a short term fix for real-time update on chat conversation
	//Once it's integrated with actual solution remove this code
	useEffect(() => {
		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this code
		if (numberListTimeoutId.current) {
			clearTimeout(numberListTimeoutId.current);
		}

		if (searchTerm === '') {
			//TODO: This is a short term fix for real-time update on chat conversation
			//Once it's integrated with actual solution remove this code
			numberListTimeoutId.current = setTimeout(
				() => fetchChatNumbersList(false),
				10000,
			);
		}

		return () => {
			if (numberListTimeoutId.current) {
				clearTimeout(numberListTimeoutId.current);
			}
		};
	}, [chatListData]);

	async function fetchChatNumbersList(enableLoading = true) {
		if (enableLoading) {
			setLoading(true);
		}
		const data: Array<ChatsResponse> = await fetchChatNumbers(
			userId ? userId : '',
			orgId ? orgId : '',
		);

		const orderedData = _.orderBy(data, 'time_stamp', 'desc');

		const latestChatData = _.uniqBy(orderedData, 'phone_number');

		const chatList: Array<ChatList> = latestChatData.map(item => {
			const timeStamps = item.time_stamp
				? `${item.time_stamp} | ${convertTo12HourTime(item.time_stamp?.split(' ')[1])}`
				: 'N/A | N/A';

			return {
				name: item.name,
				phoneNumber: item.phone_number,
				timestamp: timeStamps,
				isConsultantNeeded: item.consultant_mode ? true : false,
				unreadMessageCount:
					item.unread_count === 0 ||
					item.unread_count === '' ||
					isCurrentChatNumberRef.current === item.phone_number
						? 0
						: item.unread_count,
				language: item.lang,
				llmCost: item.total_llm_cost,
				unreadFromWamid:
					isCurrentChatNumberRef.current === item.phone_number
						? ''
						: item.unread_from_wamid || '',
			};
		});

		const newTagList: Array<Tag> = CHAT_SEARCH_TAGS.map(item => {
			const count =
				item === 'Consultant Mode'
					? chatList.filter(val => val.isConsultantNeeded).length
					: chatList.length;
			return {
				name: item,
				count: count,
			};
		});

		const newLanguageTagList: Array<Tag> = CHAT_LANGUAGE_FILTERS.map(
			lang => {
				const count =
					lang === 'All'
						? chatList.length
						: chatList.filter(val => val.language === lang).length;
				return {
					name: lang,
					count: count,
				};
			},
		);

		setChatListData(
			getFilteredChatList(
				chatList,
				selectedTag,
				selectedLanguage,
				searchTerm,
			),
		);
		setChatListAllData(chatList);
		setTagList(newTagList);
		setLanguageTagList(newLanguageTagList);
		setLoading(false);
	}

	async function onViewRecentChat(
		chat: SelectedChat,
		isViewClosed: boolean = true,
	) {
		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this if statement (do not remove the code inside the if statement just remove the statement)
		//Also remove isViewClosed parameter
		let badgeUnreadCount = 0;
		let listUnreadFromWamid = '';
		if (isViewClosed) {
			setIsChatWindow(false);
			setIsChatLoading(true);
			setSelectedChatData({});
			setNewMessageCount(0);
			setSelectedChat(chat);
			setEnableConsultantMode(chat.isConsultantNeeded);
			setIsLoadingChatFirstTime(true);
			isInitialFetchDoneRef.current = false;
			setMessageError(ERROR_INITIAL_STATES);
			setMessage('');
			setIsFileSelected(false);
			setFile(null);
			setTaggedMessage(null);
			setUnreadFromWamid('');
			setUnreadCount(0);
			openChatNumberRef.current = chat.phoneNumber;

			const matchedChat = chatListAllData.find(
				item => item.phoneNumber === chat.phoneNumber,
			);
			setCurrentChatLanguage(matchedChat?.language || '');
			setCurrentChatCost(matchedChat?.llmCost);
			badgeUnreadCount = Number(matchedChat?.unreadMessageCount) || 0;
			listUnreadFromWamid = matchedChat?.unreadFromWamid || '';

			isCurrentChatNumberRef.current = chat.phoneNumber;

			const clearUnreadCount = (list: Array<ChatList>) =>
				list.map(item =>
					item.phoneNumber === chat.phoneNumber
						? {
								...item,
								unreadMessageCount: 0,
								unreadFromWamid: '',
							}
						: item,
				);
			setChatListData(clearUnreadCount);
			setChatListAllData(clearUnreadCount);
		}

		const conversation = await fetchChats(
			chat.phoneNumber,
			userId ? userId : '',
			orgId ? orgId : '',
		);

		// User switched chats before this response came back
		if (openChatNumberRef.current !== chat.phoneNumber) {
			return;
		}

		const data: Array<ChatsResponse> = Array.isArray(conversation)
			? conversation
			: [];

		const updatedData: Array<Chats> = data.map(item => {
			const [type, msg] = Object.entries(item)[1];
			const fileData = item.file ? JSON.parse(msg) : '';
			const itemFileName =
				fileData !== '' &&
				(Object.prototype.hasOwnProperty.call(fileData, 'file_name') ||
					Object.prototype.hasOwnProperty.call(fileData, 'filename'))
					? fileData.filename || fileData.file_name
					: fileData !== '' &&
						  Object.prototype.hasOwnProperty.call(fileData, 'type')
						? fileData.type.startsWith('image')
							? 'Image File'
							: 'Pdf File'
						: undefined;

			const getMimeType = (data: {
				type?: string;
				mime_type?: string;
			}) => {
				if (data.type || data.mime_type)
					return data.type || data.mime_type;
				if (itemFileName) {
					const ext = itemFileName.split('.').pop()?.toLowerCase();
					switch (ext) {
						case 'jpg':
						case 'jpeg':
							return 'image/jpeg';
						case 'png':
							return 'image/png';
						case 'gif':
							return 'image/gif';
						case 'webp':
							return 'image/webp';
						case 'pdf':
							return 'application/pdf';
						case 'mp4':
							return 'video/mp4';
						case 'mp3':
						case 'ogg':
							return 'audio/mpeg';
					}
				}
				return '';
			};

			return {
				message: item.file ? '' : msg,
				name: chat.phoneNumber,
				timestamp: item.time_stamp,
				type: type,
				fileName: itemFileName,
				fileUrl: item.file
					? `${itemFileName}+${fileData.media_id || fileData.id || ''}+${getMimeType(fileData)}`
					: undefined,
				caption: item.file ? fileData.caption : '',
				wamid: item.wamid,
				replyToWamid: item.reply_to_wamid,
				sender: item.sender,
				llmCost: item.llm_cost,
			};
		});

		const filteredData: Array<Chats> = _.filter(
			updatedData,
			item => _.get(item, 'name') === chat.phoneNumber,
		);

		const wamidToTextMap: { [wamid: string]: string } = {};
		const wamidToOwnerMap: { [wamid: string]: string } = {};
		filteredData.forEach(item => {
			if (item.wamid) {
				wamidToTextMap[item.wamid] = truncateForPreview(
					item.message || item.fileName || 'Attachment',
					65,
				);
				wamidToOwnerMap[item.wamid] = item.type;
			}
		});

		const chatMessages: Array<ChatData> = filteredData.map(item => {
			const repliedMsg = item.replyToWamid
				? wamidToTextMap[item.replyToWamid]
				: undefined;
			const repliedMsgOwner = item.replyToWamid
				? wamidToOwnerMap[item.replyToWamid]
				: undefined;

			return {
				type: item.type,
				text: item.message,
				time: convertTo12HourTime(item.timestamp.split(' ')[1]),
				date: item.timestamp.split(' ')[0],
				fileName: item.fileName,
				fileUrl: item.fileUrl,
				caption: item.caption,
				wamid: item.wamid,
				isThisReplyMsg: Boolean(repliedMsg),
				repliedMsg: repliedMsg,
				repliedMsgOwner: repliedMsgOwner,
				sender: item.sender,
				llmCost: item.llmCost,
			};
		});

		const groupByDate = _.groupBy(chatMessages, 'date');

		// Order Object By Date
		const entries = _.toPairs(groupByDate);
		const sortedEntries = _.orderBy(
			entries,
			[entry => new Date(entry[0])],
			['asc'],
		);
		const sortedObj = _.fromPairs(sortedEntries);

		// Set the unread divider from the list data, then mark the chat as read
		if (isViewClosed) {
			const divider = getUnreadDivider(
				_.flatten(Object.values(sortedObj)),
				listUnreadFromWamid,
				badgeUnreadCount,
			);

			if (divider) {
				setUnreadFromWamid(divider.wamid);
				setUnreadCount(divider.count);
			}

			onResetChatMsgCount(chat.phoneNumber, orgId ? orgId : '');
		}

		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this if statement (do not remove the code inside the if statement just remove the statement)
		if (isViewClosed) {
			setIsChatWindow(true);
			setIsChatLoading(false);
			setTimeout(() => setIsLoadingChatFirstTime(false), 1000);
		} else {
			setIsLoadingChatFirstTime(false);
		}

		if (
			!isNewMessageSendRef.current &&
			isInitialFetchDoneRef.current &&
			previousChatDataRef.current
		) {
			const previousData = previousChatDataRef.current;
			const newMessages = getNewMessages(previousData, sortedObj);

			if (newMessages.length > 0) {
				const isAtBottom = isScrolledToBottom();
				setSelectedChatData(prevData => {
					const updatedData = { ...prevData };
					newMessages.forEach(msg => {
						const dateKey = msg.date;
						if (!updatedData[dateKey]) {
							updatedData[dateKey] = [];
						}
						updatedData[dateKey].push(msg);
					});
					return updatedData;
				});

				// Mark new messages read only if the chat is open, not marked unread and the tab is focused
				if (
					isCurrentChatNumberRef.current === chat.phoneNumber &&
					document.visibilityState === 'visible' &&
					document.hasFocus()
				) {
					onResetChatMsgCount(chat.phoneNumber, orgId ? orgId : '');
				}

				if (isAtBottom) {
					handleMarkMessagesRead();
				} else {
					setNewMessageCount(
						count =>
							count +
							newMessages.filter(msg => msg.type !== 'consultant')
								.length,
					);
				}
			}
		} else {
			setSelectedChatData(sortedObj);
			isNewMessageSendRef.current = false;
		}

		previousChatDataRef.current = sortedObj;

		if (!isInitialFetchDoneRef.current) {
			isInitialFetchDoneRef.current = true; // Mark initial fetch as done
		}

		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this code
		if (timeoutId.current) {
			clearTimeout(timeoutId.current);
		}

		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this code
		timeoutId.current = setTimeout(
			() => onViewRecentChat(chat, false),
			10000,
		);
	}

	function normalizeMessage(msg: ChatData) {
		return {
			...msg,
			text: msg.text.trim().toLowerCase(),
			time: msg.time.trim(),
		};
	}

	function getNewMessages(
		previousData: { [index: string]: Array<ChatData> },
		newData: { [index: string]: Array<ChatData> },
	): ChatData[] {
		const newMessages: ChatData[] = [];

		Object.keys(newData).forEach(date => {
			if (!previousData[date]) {
				newMessages.push(...newData[date]);
			} else {
				newData[date].forEach(newMsg => {
					const normalizedNewMsg = normalizeMessage(newMsg);

					const isNewMessage = !previousData[date].some(prevMsg => {
						const normalizedPrevMsg = normalizeMessage(prevMsg);
						return (
							normalizedPrevMsg.time === normalizedNewMsg.time &&
							normalizedPrevMsg.text === normalizedNewMsg.text
						);
					});

					if (isNewMessage) {
						newMessages.push(newMsg);
					}
				});
			}
		});

		return newMessages;
	}

	async function getUrlForImage(mediaId: string) {
		const response = await downloadMediaFile(
			mediaId,
			userId ? userId : '',
			orgId ? orgId : '',
		);

		if (response.error) {
			console.error(
				'Error downloading the media file:',
				response.message,
			);
			return;
		}

		return response.data.url;
	}

	function onSearchChatList(search: string): void {
		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this code
		//We are stopping the timeout to show the search results once the search term is empty it will timeout will resume
		if (numberListTimeoutId.current) {
			clearTimeout(numberListTimeoutId.current);
		}

		setSearchTerm(search);

		setChatListData(
			getFilteredChatList(
				chatListAllData,
				selectedTag,
				selectedLanguage,
				search,
			),
		);
	}

	function handleMarkMessagesRead() {
		enableAutoScrollRef.current = true;
		setNewMessageCount(0);
		setTimeout(() => {
			enableAutoScrollRef.current = false;
		}, 1000);
	}

	function onChangeGridVisibility() {
		setShowNumberList(!showNumberList);
	}

	async function onHandelConsultantMode(
		event: React.ChangeEvent<HTMLInputElement>,
	) {
		const response = await onEnableAgentMode(
			selectedChat.phoneNumber,
			event.target.checked ? 'on' : 'off',
			orgId ? orgId : '',
		);

		if (!response.error) {
			setEnableConsultantMode(!event.target.checked);
			fetchChatNumbersList(false);
			setMessage('');
			setIsFileSelected(false);
			setMessageError(ERROR_INITIAL_STATES);
			setTaggedMessage(null);
		}

		onAddClickStats(
			`Consultant mode ${event.target.checked ? 'on' : 'off'} button`,
		);
	}

	function onFilterNumberUsingTag(tag: string) {
		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this code
		if (numberListTimeoutId.current) {
			clearTimeout(numberListTimeoutId.current);
		}
		setSelectedTag(tag);
		setChatListData(
			getFilteredChatList(chatListAllData, tag, selectedLanguage, ''),
		);

		if (searchTerm !== '') {
			setSearchTerm('');
		}
	}

	function onSelectLanguage(language: string) {
		setSelectedLanguage(language);
		setChatListData(
			getFilteredChatList(
				chatListAllData,
				selectedTag,
				language,
				searchTerm,
			),
		);
	}

	function onChangeMessage(value: string) {
		setMessage(value);
		setMessageError(ERROR_INITIAL_STATES);
	}

	async function onSelectChatLanguage(language: string) {
		const previousLanguage = currentChatLanguage;
		setCurrentChatLanguage(language);

		const response = await updateChatLanguage(
			selectedChat.phoneNumber,
			language,
			orgId ? orgId : '',
		);

		if (response.error) {
			setCurrentChatLanguage(previousLanguage);
		}
	}

	function onTagMessage(taggedMsg: TaggedMessage) {
		setTaggedMessage(taggedMsg);
		setMessageError(ERROR_INITIAL_STATES);

		setTimeout(() => {
			if (messagesEndRef.current) {
				messagesEndRef.current.scrollTop =
					messagesEndRef.current.scrollHeight;
			}
		}, 0);
	}

	function onCancelTaggedMessage() {
		setTaggedMessage(null);
	}

	function setChatUnreadBadge(phoneNumber: string, count: number) {
		const updateBadge = (list: Array<ChatList>) =>
			list.map(item =>
				item.phoneNumber === phoneNumber
					? { ...item, unreadMessageCount: count }
					: item,
			);
		setChatListData(updateBadge);
		setChatListAllData(updateBadge);
	}

	async function onMarkMessageUnread(wamid: string) {
		const phoneNumber = selectedChat.phoneNumber;
		const previousUnreadFromWamid = unreadFromWamid;
		const previousUnreadCount = unreadCount;

		// Show the divider and badge right away instead of waiting for the next poll
		const messages = _.flatten(Object.values(selectedChatData));
		const markedIndex = messages.findIndex(item => item.wamid === wamid);
		const count =
			markedIndex === -1
				? 1
				: messages
						.slice(markedIndex)
						.filter(item => item.type === 'human').length || 1;

		setUnreadFromWamid(wamid);
		setUnreadCount(count);
		setChatUnreadBadge(phoneNumber, count);
		if (isCurrentChatNumberRef.current === phoneNumber) {
			isCurrentChatNumberRef.current = '';
		}

		const response = await markMessageAsUnread(
			phoneNumber,
			wamid,
			orgId ? orgId : '',
		);

		if (!response.error) {
			fetchChatNumbersList(false);
		} else {
			setUnreadFromWamid(previousUnreadFromWamid);
			setUnreadCount(previousUnreadCount);
			setChatUnreadBadge(phoneNumber, 0);
			isCurrentChatNumberRef.current = phoneNumber;
			setMessageError({
				status: true,
				msg: 'Failed to mark message as unread',
			});
		}
	}

	async function onSendMessage() {
		if (isSending) {
			return;
		}

		try {
			const response = await sendMessage(
				selectedChat.phoneNumber,
				message,
				orgId ? orgId : '',
				taggedMessage?.wamid,
			);

			if (!response.error) {
				const currentDate = new Date();
				const todayDate = currentDate.toISOString().split('T')[0];
				const newMessage: ChatData = {
					type: 'consultant',
					text: message,
					time: currentDate.toLocaleTimeString([], {
						hour: '2-digit',
						minute: '2-digit',
					}),
					date: todayDate,
					fileName: undefined,
					fileUrl: undefined,
					isFileIsImage: false,
					imageURL: '',
					caption: '',
					isThisReplyMsg: Boolean(taggedMessage),
					repliedMsg: taggedMessage
						? taggedMessage.isFile
							? taggedMessage.fileName || 'Attachment'
							: taggedMessage.text
						: '',
					repliedMsgOwner: taggedMessage?.chatOwner,
					isMsgHasReaction: false,
					reaction: '',
				};

				const isDateExist =
					Object.keys(selectedChatData).includes(todayDate);
				let clone;
				if (isDateExist) {
					clone = {
						...selectedChatData,
						[todayDate]: [
							...selectedChatData[todayDate],
							newMessage,
						],
					};
				} else {
					clone = {
						...selectedChatData,
						[todayDate]: [newMessage],
					};
				}

				setSelectedChatData(clone);
				setMessage('');
				setTaggedMessage(null);
				setUnreadFromWamid('');
				setIsLoadingChatFirstTime(true);
				isNewMessageSendRef.current = true;
				fetchMsgDetails();
			} else {
				setMessageError({ status: true, msg: 'Something went wrong' });
			}
		} catch (error) {
			setMessageError({ status: true, msg: 'Failed to send message' });
		} finally {
			setIsSending(false);
		}
	}

	async function onSendUploadedFile(uploadingFile: File) {
		if (isSending) {
			return;
		}

		try {
			const uploadData = await onFetchUploadInfo();
			const response = await uploadPdf(
				uploadingFile,
				uploadData.version,
				uploadData.phone_id,
				uploadData.token,
				userId ? userId : '',
				orgId ? orgId : '',
			);

			if (!response.error) {
				const uploadResponse = await sendUploadIdAndMessage(
					response.data.id,
					selectedChat.phoneNumber,
					message,
					uploadingFile.name,
					orgId ? orgId : '',
					taggedMessage?.wamid,
				);

				if (!uploadResponse.error) {
					const currentDate = new Date();
					const todayDate = currentDate.toISOString().split('T')[0];
					const isImage = uploadingFile.type.startsWith('image');
					const newMessage: ChatData = {
						type: 'consultant',
						text: '',
						time: currentDate.toLocaleTimeString([], {
							hour: '2-digit',
							minute: '2-digit',
						}),
						date: todayDate,
						fileName: uploadingFile.name,
						fileUrl: `${uploadingFile.name}+${response.data.id}`,
						caption: message,
						isFileIsImage: isImage,
						imageURL: isImage ? previewUrl : undefined,
						isThisReplyMsg: Boolean(taggedMessage),
						repliedMsg: taggedMessage
							? taggedMessage.isFile
								? taggedMessage.fileName || 'Attachment'
								: taggedMessage.text
							: '',
						repliedMsgOwner: taggedMessage?.chatOwner,
					};

					const isDateExist =
						Object.keys(selectedChatData).includes(todayDate);
					let clone;
					if (isDateExist) {
						clone = {
							...selectedChatData,
							[todayDate]: [
								...selectedChatData[todayDate],
								newMessage,
							],
						};
					} else {
						clone = {
							...selectedChatData,
							[todayDate]: [newMessage],
						};
					}

					setSelectedChatData(clone);
					setFile(null);
					setMessage('');
					setIsFileSelected(false);
					setPreviewUrl('');
					setTaggedMessage(null);
					setUnreadFromWamid('');
					setIsLoadingChatFirstTime(true);
					isNewMessageSendRef.current = true;
				} else {
					setMessageError({
						status: true,
						msg: 'Error sending the file',
					});
				}
			} else {
				setMessageError({
					status: true,
					msg: 'Error sending the file',
				});
			}
		} catch (error) {
			setMessageError({ status: true, msg: 'Failed to send message' });
		} finally {
			setIsSending(false);
		}
	}

	async function onFetchConsultantSuggestion() {
		if (isFetchingSuggestion || !selectedChat.phoneNumber) {
			return;
		}

		setIsFetchingSuggestion(true);
		setMessageError(ERROR_INITIAL_STATES);

		try {
			const response: ConsultantDraftsResponse =
				await fetchConsultantSuggestedResponse(
					selectedChat.phoneNumber,
					orgId ? orgId : '',
					taggedMessage?.wamid,
				);

			const suggestion =
				response.draft?.working_text || response.draft?.text;

			if (!response.error && suggestion) {
				setMessage(suggestion);

				setTimeout(() => {
					const input = messageInputRef.current;
					if (input) {
						input.focus();
						input.setSelectionRange(
							suggestion.length,
							suggestion.length,
						);
					}
				}, 0);
			} else {
				setMessageError({
					status: true,
					msg: 'No suggested response available',
				});
			}
		} catch (error) {
			setMessageError({
				status: true,
				msg: 'Failed to fetch suggested response',
			});
		} finally {
			setIsFetchingSuggestion(false);
		}
	}

	async function onSendChat() {
		if (!isFileSelected && (!message || message === '')) {
			return;
		}

		setIsSending(true);

		if (isFileSelected && file) {
			onSendUploadedFile(file);
		} else {
			onSendMessage();
		}
	}

	function onCancelSelectedFile() {
		setIsFileSelected(false);
		setFile(null);
		setMessageError(ERROR_INITIAL_STATES);
	}

	function onSelectAttachments(event: React.ChangeEvent<HTMLInputElement>) {
		const selectedFile = event.target.files ? event.target.files[0] : null;

		if (selectedFile) {
			if (
				selectedFile.type !== 'application/pdf' &&
				selectedFile.type !== 'image/png' &&
				selectedFile.type !== 'image/jpeg'
			) {
				setMessageError({
					status: true,
					msg: 'Invalid file format. only png, jpeg and pdf files are supported',
				});
				return;
			}

			setFile(selectedFile);
			setIsFileSelected(true);
			setMessageError(ERROR_INITIAL_STATES);
			if (selectedFile.type.startsWith('image')) {
				const preview = URL.createObjectURL(selectedFile);
				setPreviewUrl(preview);
			} else {
				setPreviewUrl('');
			}

			if (event.target.value) {
				event.target.value = '';
			}
		}
	}

	async function onDownloadFileWithUrl(url: string) {
		setIsDownloading(true);
		setDownloadingFile(url);
		const downloadData = url.split('+');
		const response = await downloadMediaFile(
			downloadData[1],
			userId ? userId : '',
			orgId ? orgId : '',
		);

		if (response.error) {
			console.error(
				'Error downloading the media file:',
				response.message,
			);
			return;
		}

		const blobData = response.data;
		const contentType =
			downloadData[2] !== '' ? downloadData[2] : 'application/pdf';
		const blob = new Blob([blobData], { type: contentType });
		const downloadLink = document.createElement('a');
		downloadLink.href = URL.createObjectURL(blob);
		downloadLink.download = downloadData[0];
		downloadLink.click();
		URL.revokeObjectURL(downloadLink.href);
		setIsDownloading(false);
		setDownloadingFile('');
	}

	function isScrolledToBottom() {
		if (!messagesEndRef.current) {
			return false;
		}

		return (
			messagesEndRef.current.scrollTop +
				messagesEndRef.current.clientHeight >=
			messagesEndRef.current.scrollHeight - 20
		);
	}

	const handleScroll = throttle(() => {
		if (isScrolledToBottom()) {
			setNewMessageCount(0);
		}
	}, 100);

	function onAddClickStats(item: string) {
		onAddClickStat(
			userId ? userId : '',
			item,
			true,
			'Conversations',
			getUserEmail() ? getUserEmail() : 'N/A',
			'N/A',
		);
	}

	function renderBodyContent() {
		if (isMobile) {
			if (showNumberList) {
				return (
					<Grid2
						container
						marginLeft={'10px'}
						columnSpacing={2}
						height={'85vh'}
						rowSpacing={{ xs: 2 }}>
						<ChatNumbersList
							chatListData={chatListData}
							onSearchChatList={onSearchChatList}
							onViewRecentChat={onViewRecentChat}
							selectedPhoneNumber={selectedChat.phoneNumber}
							loading={loading}
							onChangeGridVisibility={onChangeGridVisibility}
							tagList={tagList}
							onSelectTag={onFilterNumberUsingTag}
							selectedTag={selectedTag}
							languageTagList={languageTagList}
							onSelectLanguage={onSelectLanguage}
							selectedLanguage={selectedLanguage}
							searchedText={searchTerm}
							isMobile={isMobile}
						/>
					</Grid2>
				);
			} else {
				return (
					<Grid2
						container
						marginLeft={'10px'}
						columnSpacing={2}
						height={'85vh'}
						rowSpacing={{ xs: 2 }}>
						<ForwardedChatWindowMain
							isChatWindow={isChatWindow}
							selectedChatData={selectedChatData}
							ref={messagesEndRef}
							isChatLoading={isChatLoading}
							isMobile={isMobile}
							onclickGoBack={onChangeGridVisibility}
							message={message}
							setMessage={onChangeMessage}
							isConsultantModeOn={enableConsultantMode}
							onChangeConsultantMode={onHandelConsultantMode}
							selectedChat={selectedChat}
							onSendChat={onSendChat}
							messageError={messageError}
							isFileSelected={isFileSelected}
							selectedFile={file}
							onCancelSelectedFile={onCancelSelectedFile}
							onSelectAttachments={onSelectAttachments}
							previewUrl={previewUrl}
							onDownloadFile={onDownloadFileWithUrl}
							newMessageCount={newMessageCount}
							onScrollDown={handleMarkMessagesRead}
							isInitialFetchDone={isInitialFetchDoneRef.current}
							handleScroll={handleScroll}
							isSending={isSending}
							isDownloading={isDownloading}
							downloadingFile={downloadingFile}
							isFetchingSuggestion={isFetchingSuggestion}
							onFetchConsultantSuggestion={
								onFetchConsultantSuggestion
							}
							taggedMessage={taggedMessage}
							onTagMessage={onTagMessage}
							onCancelTaggedMessage={onCancelTaggedMessage}
							onMarkMessageUnread={onMarkMessageUnread}
							currentChatLanguage={currentChatLanguage}
							onSelectChatLanguage={onSelectChatLanguage}
							currentChatCost={currentChatCost}
							messageInputRef={messageInputRef}
							unreadFromWamid={unreadFromWamid}
							unreadCount={unreadCount}
						/>
					</Grid2>
				);
			}
		} else {
			return (
				<Grid2
					container
					paddingLeft={'20px'}
					paddingRight={'20px'}
					flexDirection={'column'}>
					<Grid2
						container
						marginLeft={'10px'}
						columnSpacing={1}
						height={'85vh'}
						rowSpacing={{ xs: 1 }}>
						<ChatNumbersList
							chatListData={chatListData}
							onSearchChatList={onSearchChatList}
							onViewRecentChat={onViewRecentChat}
							selectedPhoneNumber={selectedChat.phoneNumber}
							loading={loading}
							onChangeGridVisibility={onChangeGridVisibility}
							tagList={tagList}
							onSelectTag={onFilterNumberUsingTag}
							selectedTag={selectedTag}
							languageTagList={languageTagList}
							onSelectLanguage={onSelectLanguage}
							selectedLanguage={selectedLanguage}
							searchedText={searchTerm}
						/>
						<ForwardedChatWindowMain
							isChatWindow={isChatWindow}
							selectedChatData={selectedChatData}
							ref={messagesEndRef}
							isChatLoading={isChatLoading}
							isMobile={isMobile}
							onclickGoBack={onChangeGridVisibility}
							message={message}
							setMessage={onChangeMessage}
							isConsultantModeOn={enableConsultantMode}
							onChangeConsultantMode={onHandelConsultantMode}
							selectedChat={selectedChat}
							onSendChat={onSendChat}
							messageError={messageError}
							isFileSelected={isFileSelected}
							selectedFile={file}
							onCancelSelectedFile={onCancelSelectedFile}
							onSelectAttachments={onSelectAttachments}
							previewUrl={previewUrl}
							onDownloadFile={onDownloadFileWithUrl}
							newMessageCount={newMessageCount}
							onScrollDown={handleMarkMessagesRead}
							isInitialFetchDone={isInitialFetchDoneRef.current}
							handleScroll={handleScroll}
							isSending={isSending}
							isDownloading={isDownloading}
							downloadingFile={downloadingFile}
							isFetchingSuggestion={isFetchingSuggestion}
							onFetchConsultantSuggestion={
								onFetchConsultantSuggestion
							}
							taggedMessage={taggedMessage}
							onTagMessage={onTagMessage}
							onCancelTaggedMessage={onCancelTaggedMessage}
							onMarkMessageUnread={onMarkMessageUnread}
							currentChatLanguage={currentChatLanguage}
							onSelectChatLanguage={onSelectChatLanguage}
							currentChatCost={currentChatCost}
							messageInputRef={messageInputRef}
							unreadFromWamid={unreadFromWamid}
							unreadCount={unreadCount}
						/>
					</Grid2>
				</Grid2>
			);
		}
	}

	return (
		<>
			<SideNavigationBar />
			<div style={mainDivStyle}>{renderBodyContent()}</div>
		</>
	);
}

export default Conversations;
